import type { IRealChordRef } from '../ireal/types';
import type { Score } from '../score/types';

/** MusicXML's parentheses-degrees concerns alterations, not whole chords.
 * Keep group boundaries in a standard miscellaneous field without changing
 * the root/kind that other readers play. Positions count emitted harmonies,
 * including alternates and N.C., rather than app-specific chord IDs. */
export const MUSICXML_CHORD_PARENTHESES_FIELD = 'scoreguard-chord-parentheses';
type Parentheses = NonNullable<IRealChordRef['parentheses']>;
type Marker = [measureIndex: number, harmonyIndex: number, parentheses: Parentheses];

export function chordParenthesesMetadata(score: Score): string | undefined {
  const markers: Marker[] = [];
  score.form.forEach((measure, measureIndex) => {
    let harmonyIndex = 0;
    const append = (chord: IRealChordRef) => {
      if (!chord.chordType || !chord.root) return;
      if (chord.parentheses) markers.push([measureIndex, harmonyIndex, chord.parentheses]);
      harmonyIndex++;
    };
    for (const chord of measure.harmony) {
      if (chord.noChord) harmonyIndex++;
      else if (chord.chordType && chord.root) {
        append(chord);
        if (chord.alternate) append(chord.alternate);
      }
    }
  });
  return markers.length ? JSON.stringify(markers) : undefined;
}

export function parseChordParenthesesMetadata(
  value: string | null | undefined,
): Map<string, Parentheses> {
  const result = new Map<string, Parentheses>();
  if (!value) return result;
  try {
    const markers: unknown = JSON.parse(value);
    if (!Array.isArray(markers)) return result;
    for (const marker of markers) {
      if (!Array.isArray(marker) || marker.length !== 3) continue;
      const [measure, harmony, parentheses] = marker;
      if (!Number.isInteger(measure) || measure < 0 || !Number.isInteger(harmony) || harmony < 0)
        continue;
      if (parentheses !== 'start' && parentheses !== 'stop' && parentheses !== 'both') continue;
      result.set(`${measure}:${harmony}`, parentheses);
    }
  } catch {
    // Foreign or malformed metadata must not prevent importing the music.
  }
  return result;
}
